import React, { useMemo, useState, useEffect } from "react";
import {
  Box, Button, Chip, CircularProgress, Divider, Grid, IconButton, Link, Stack, TextField, Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { PageHeader, SectionCard } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";

const EMPTY_EXPENSE = {
  date: "", description: "",
  lodging: "", boarding: "", food: "", conveyance: "", Material_Purchase: "", PO: "", Invoice: "", other: "",
  note: "", files: [], existingFiles: [],
};

const EXPENSE_FIELDS = [
  { key: "lodging", label: "Lodging" },
  { key: "boarding", label: "Boarding" },
  { key: "food", label: "Food" },
  { key: "conveyance", label: "Conveyance" },
  { key: "Material_Purchase", label: "Materials" },
  { key: "PO", label: "Purchase Order" },
  { key: "Invoice", label: "Invoice" },
  { key: "other", label: "Other" },
];

const rowTotal = (row) =>
  EXPENSE_FIELDS.reduce((sum, f) => sum + (parseFloat(row[f.key]) || 0), 0);

const toDateInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
};

const numOrEmpty = (v) => (v === 0 || v ? String(v) : "");

export default function ReimbursementFormNew() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const isEdit = Boolean(editId);

  const [form, setForm] = useState({
    fromDate: "", toDate: "",
    managerName: "", department: "", location: "",
    employeeName: "", clientName: "", purpose: "",
    expenses: [{ ...EMPTY_EXPENSE }],
  });
  const [loading, setLoading] = useState(false);
  const [loadingClaim, setLoadingClaim] = useState(isEdit);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Auto-fill the claimant's own details from their profile (create mode only /
  // or as fallback when edit fields are empty).
  useEffect(() => {
    if (isEdit) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [meRes, dirRes] = await Promise.all([
          api.get("/api/self-service/me"),
          api.get("/api/self-service/directory").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const me = meRes.data?.data || meRes.data || {};
        const dir = Array.isArray(dirRes.data) ? dirRes.data : dirRes.data?.data || [];
        const mgr = dir.find((d) => String(d.employeeid) === String(me.manager));
        setForm((f) => ({
          ...f,
          employeeName: f.employeeName || me.name || "",
          department: f.department || me.department || "",
          location: f.location || me.jobLocation || "",
          managerName: f.managerName || mgr?.name || me.manager || "",
        }));
        if (me.name) setProfileLoaded(true);
      } catch (_) {
        /* profile fetch failed — leave the fields empty and editable */
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit]);

  // Prefill when editing an existing pending claim.
  useEffect(() => {
    if (!editId) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingClaim(true);
      try {
        const res = await api.get(endpoints.reimbursement.byId(editId));
        if (cancelled) return;
        const claim = res.data;
        const expenses = Array.isArray(claim.expenses) ? claim.expenses : [];
        const allPending = expenses.every((e) => String(e.status || "pending").toLowerCase() === "pending");
        if (!allPending) {
          toast.error("Only pending reimbursements can be edited");
          nav("/my-reimbursements");
          return;
        }
        setForm({
          fromDate: toDateInput(claim.fromDate),
          toDate: toDateInput(claim.toDate),
          managerName: claim.managerName || "",
          department: claim.department || "",
          location: claim.location || "",
          employeeName: claim.employeeName || "",
          clientName: claim.clientName || "",
          purpose: claim.purpose || "",
          expenses: expenses.length
            ? expenses.map((exp) => ({
                date: toDateInput(exp.date),
                description: exp.description || "",
                lodging: numOrEmpty(exp.lodging),
                boarding: numOrEmpty(exp.boarding),
                food: numOrEmpty(exp.food),
                conveyance: numOrEmpty(exp.conveyance),
                Material_Purchase: numOrEmpty(exp.materialPurchase ?? exp.Material_Purchase),
                PO: numOrEmpty(exp.PO),
                Invoice: numOrEmpty(exp.Invoice),
                other: numOrEmpty(exp.other),
                note: exp.note || "",
                files: [],
                existingFiles: Array.isArray(exp.files) ? [...exp.files] : [],
              }))
            : [{ ...EMPTY_EXPENSE }],
        });
        setProfileLoaded(true);
      } catch (err) {
        toast.error(extractErrorMessage(err));
        nav("/my-reimbursements");
      } finally {
        if (!cancelled) setLoadingClaim(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, nav]);

  const grandTotal = useMemo(
    () => form.expenses.reduce((sum, e) => sum + rowTotal(e), 0),
    [form.expenses]
  );

  const updateField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateExpense = (idx, k, v) => {
    setForm((f) => {
      const expenses = [...f.expenses];
      expenses[idx] = { ...expenses[idx], [k]: v };
      return { ...f, expenses };
    });
  };
  const addRow = () => setForm((f) => ({ ...f, expenses: [...f.expenses, { ...EMPTY_EXPENSE }] }));
  const removeRow = (idx) => setForm((f) => ({ ...f, expenses: f.expenses.filter((_, i) => i !== idx) }));
  const removeExistingFile = (idx, fileIdx) => {
    setForm((f) => {
      const expenses = [...f.expenses];
      const existingFiles = [...(expenses[idx].existingFiles || [])];
      existingFiles.splice(fileIdx, 1);
      expenses[idx] = { ...expenses[idx], existingFiles };
      return { ...f, expenses };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employeeName.trim()) return toast.error("Employee name is required");
    setLoading(true);
    try {
      const fd = new FormData();
      ["fromDate", "toDate", "managerName", "department", "location", "employeeName", "clientName", "purpose"].forEach((k) => {
        fd.append(k, form[k] || "");
      });
      form.expenses.forEach((exp, i) => {
        fd.append(`expenses[${i}][date]`, exp.date || "");
        fd.append(`expenses[${i}][description]`, exp.description || "");
        EXPENSE_FIELDS.forEach((f) => fd.append(`expenses[${i}][${f.key}]`, exp[f.key] || "0"));
        fd.append(`expenses[${i}][total]`, String(rowTotal(exp)));
        fd.append(`expenses[${i}][note]`, exp.note || "");
        if (isEdit) {
          fd.append(`expenses[${i}][existingFiles]`, JSON.stringify(exp.existingFiles || []));
        }
        (exp.files || []).forEach((file) => fd.append(`expenses[${i}][files][]`, file));
      });
      if (isEdit) {
        await api.put(endpoints.reimbursement.update(editId), fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Reimbursement updated");
      } else {
        await api.post(endpoints.reimbursement.create, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Reimbursement submitted");
      }
      nav("/my-reimbursements");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  if (loadingClaim) {
    return (
      <Box p={3} display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <PageHeader
        title={isEdit ? "Edit Reimbursement" : "New Reimbursement"}
        subtitle={isEdit ? "Update your pending expense claim" : "Submit expenses for approval and payment"}
        icon={<ReceiptLongOutlinedIcon />}
      />

      <Box component="form" onSubmit={submit}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <SectionCard title="Request Details" subtitle="Who is claiming and why" accent="#6C50BF">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Employee Name *" value={form.employeeName} onChange={updateField("employeeName")} required helperText={profileLoaded && !isEdit ? "Auto-filled from your profile" : " "} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Manager Name" value={form.managerName} onChange={updateField("managerName")} helperText={profileLoaded && !isEdit ? "Auto-filled — edit if needed" : " "} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Department" value={form.department} onChange={updateField("department")} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Location" value={form.location} onChange={updateField("location")} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="From Date" InputLabelProps={{ shrink: true }} value={form.fromDate} onChange={updateField("fromDate")} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="To Date" InputLabelProps={{ shrink: true }} value={form.toDate} onChange={updateField("toDate")} inputProps={{ min: form.fromDate }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Client Name (optional)" value={form.clientName} onChange={updateField("clientName")} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Purpose" value={form.purpose} onChange={updateField("purpose")} placeholder="Client visit, team offsite…" /></Grid>
              </Grid>
            </SectionCard>

            <Box mt={3}>
              <SectionCard
                title="Expense Items"
                subtitle={`${form.expenses.length} ${form.expenses.length === 1 ? "item" : "items"}`}
                accent="#39C645"
                actions={[
                  <Button key="add" variant="outlined" size="small" startIcon={<AddIcon />} onClick={addRow}>Add row</Button>,
                ]}
              >
                <Stack spacing={2}>
                  {form.expenses.map((exp, idx) => {
                    const total = rowTotal(exp);
                    return (
                      <Box key={idx} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider", bgcolor: "background.default", position: "relative" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={`#${idx + 1}`} sx={{ bgcolor: "primary.main", color: "#fff", fontWeight: 700 }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Expense entry</Typography>
                          </Stack>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.main" }}>₹{total.toFixed(2)}</Typography>
                            {form.expenses.length > 1 && (
                              <IconButton size="small" color="error" onClick={() => removeRow(idx)}>
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        </Stack>

                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={exp.date} onChange={(e) => updateExpense(idx, "date", e.target.value)} inputProps={{ max: new Date().toISOString().slice(0, 10) }} /></Grid>
                          <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Description" value={exp.description} onChange={(e) => updateExpense(idx, "description", e.target.value)} /></Grid>
                          {EXPENSE_FIELDS.map((f) => (
                            <Grid item xs={6} sm={3} key={f.key}>
                              <TextField
                                fullWidth size="small" type="number" label={f.label} value={exp[f.key]}
                                onChange={(e) => updateExpense(idx, f.key, e.target.value)}
                                inputProps={{ min: 0, step: "0.01" }}
                                placeholder="0"
                              />
                            </Grid>
                          ))}
                          <Grid item xs={12} sm={8}><TextField fullWidth size="small" label="Note" value={exp.note} onChange={(e) => updateExpense(idx, "note", e.target.value)} /></Grid>
                          <Grid item xs={12} sm={4}>
                            <Button
                              component="label" variant="outlined" fullWidth size="small"
                              startIcon={<CloudUploadOutlinedIcon />}
                              sx={{ height: 40, justifyContent: "flex-start" }}
                            >
                              {exp.files?.length > 0 ? `${exp.files.length} new file(s)` : "Attach"}
                              <input type="file" hidden multiple accept="image/*,.pdf" onChange={(e) => updateExpense(idx, "files", [...(exp.files || []), ...Array.from(e.target.files || [])])} />
                            </Button>
                          </Grid>
                          {!!exp.existingFiles?.length && (
                            <Grid item xs={12}>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, color: "text.secondary" }}>
                                Existing receipts
                              </Typography>
                              <Stack spacing={0.5}>
                                {exp.existingFiles.map((url, fi) => (
                                  <Stack key={`${url}-${fi}`} direction="row" spacing={1} alignItems="center">
                                    <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12 }}>
                                      Receipt {fi + 1}
                                    </Link>
                                    <IconButton size="small" color="error" onClick={() => removeExistingFile(idx, fi)}>
                                      <DeleteOutlineOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                ))}
                              </Stack>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    );
                  })}
                </Stack>
              </SectionCard>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box sx={{ position: { lg: "sticky" }, top: { lg: 88 } }}>
              <SectionCard title="Summary" accent="#F9BC06">
                <Stack spacing={1.5}>
                  {EXPENSE_FIELDS.map((f) => {
                    const sum = form.expenses.reduce((s, e) => s + (parseFloat(e[f.key]) || 0), 0);
                    if (!sum) return null;
                    return (
                      <Stack key={f.key} direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{f.label}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>₹{sum.toFixed(2)}</Typography>
                      </Stack>
                    );
                  })}
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Grand Total</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "primary.main" }}>₹{grandTotal.toFixed(2)}</Typography>
                  </Stack>
                  <Button
                    type="submit" variant="contained" size="large" fullWidth
                    disabled={loading || grandTotal === 0 || !form.employeeName}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : (isEdit ? <SaveOutlinedIcon /> : <SendOutlinedIcon />)}
                    sx={{ py: 1.25, mt: 1 }}
                  >
                    {loading ? (isEdit ? "Saving…" : "Submitting…") : (isEdit ? "Save Changes" : "Submit Request")}
                  </Button>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", textAlign: "center" }}>
                    {isEdit ? "Changes stay pending until your manager reviews them." : "Your manager will be notified for approval."}
                  </Typography>
                  {isEdit && (
                    <Button variant="text" size="small" onClick={() => nav("/my-reimbursements")}>
                      Cancel
                    </Button>
                  )}
                </Stack>
              </SectionCard>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
